import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { documentAPI } from '../../../services/documentAPI';
import { platformAPI } from '../../../services/platformAPI';

const TenderAiAssistantTab = ({ tenderId }) => {
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [docs, setDocs] = useState([]);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);

  useEffect(() => {
    platformAPI.listAiSessions({ tenderId }).then((r) => {
      const list = r.data?.data?.sessions || [];
      setSessions(list);
      if (list[0]) {
        setSessionId(list[0]._id);
        setMessages(list[0].messages || []);
      }
    });
    documentAPI.getDocuments({ tenderId, limit: 50 }).then((r) => {
      setDocs(r.data?.data?.documents || r.data?.documents || []);
    }).catch(() => setDocs([]));
  }, [tenderId]);

  const startSession = async () => {
    setLoading(true);
    try {
      const r = await platformAPI.createAiSession({
        tenderId,
        title: `Tender ${tenderId.slice(-6)} Q&A`,
        documentIds: selectedDocIds
      });
      const s = r.data?.data?.session;
      setSessionId(s._id);
      setMessages(s.messages || []);
      setSessions([s, ...sessions]);
    } finally {
      setLoading(false);
    }
  };

  const ask = async () => {
    if (!sessionId || !question.trim()) return;
    setLoading(true);
    setNotConfigured(false);
    try {
      const r = await platformAPI.askAi(sessionId, question);
      const s = r.data?.data?.session;
      setMessages(s.messages || []);
      if (r.data?.data?.configured === false) setNotConfigured(true);
      setQuestion('');
    } finally {
      setLoading(false);
    }
  };

  const toggleDoc = (id) => {
    setSelectedDocIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <div>
      <p className="text-muted small">Ask questions about authorised tender documents. Answers cite sources; commercial content requires commercial permissions.</p>
      {notConfigured ? (
        <Alert variant="warning">
          AI is not configured. <Link to="/apps/tender/settings/ai">Choose Gemini or OpenAI in Settings</Link>.
        </Alert>
      ) : null}
      <div className="row">
        <div className="col-md-3">
          <h3 className="h6">Conversations</h3>
          <ListGroup className="mb-2">
            {sessions.map((s) => (
              <ListGroup.Item key={s._id} action active={s._id === sessionId} onClick={() => { setSessionId(s._id); setMessages(s.messages || []); }}>
                {s.title || 'Q&A'}
              </ListGroup.Item>
            ))}
          </ListGroup>
          <Button size="sm" variant="outline-primary" disabled={loading || !selectedDocIds.length} onClick={startSession}>New conversation</Button>
        </div>
        <div className="col-md-4">
          <h3 className="h6">Documents</h3>
          {docs.map((d) => (
            <Form.Check key={d._id} type="checkbox" label={d.title || d.fileName} checked={selectedDocIds.includes(d._id)} onChange={() => toggleDoc(d._id)} />
          ))}
          {!docs.length ? <p className="small text-muted">No documents for this tender.</p> : null}
        </div>
        <div className="col-md-5">
          <div className="border rounded p-2 mb-2" style={{ minHeight: 200, maxHeight: 320, overflow: 'auto' }}>
            {messages.map((m, i) => (
              <div key={i} className={`small mb-2 ${m.role === 'user' ? 'text-end' : ''}`}>
                <strong>{m.role === 'user' ? 'You' : 'Assistant'}:</strong> {m.content}
                {m.sourceRefs?.length ? (
                  <div className="text-muted">Sources: {m.sourceRefs.map((ref) => ref.title || ref.documentId).join(', ')}</div>
                ) : null}
              </div>
            ))}
          </div>
          <Form.Control as="textarea" rows={2} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask about the tender documents…" />
          <Button className="mt-2" size="sm" disabled={loading || !sessionId} onClick={ask}>
            {loading ? <Spinner size="sm" /> : 'Ask'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TenderAiAssistantTab;
